import React, { useState } from 'react';
import { TrendingUp, Info } from 'lucide-react';

interface Pace {
  perMile: string;
  perKm: string;
}

const PaceCalculator: React.FC = () => {
  const [fiveKTime, setFiveKTime] = useState('');
  const [paces, setPaces] = useState<{ tPace: Pace; iPace: Pace } | null>(null);

  const calculatePaces = () => {
    const baseTime = 20 * 60;
    const baseTPaceMile = 6 * 60 + 53;
    const baseIPaceMile = 6 * 60 + 19;

    const [minutes, seconds] = fiveKTime.split(':').map(Number);
    const inputTime = minutes * 60 + seconds;

    const ratio = inputTime / baseTime;
    const tPaceMileSec = baseTPaceMile * ratio;
    const iPaceMileSec = baseIPaceMile * ratio;

    const formatPace = (sec: number) => {
      const min = Math.floor(sec / 60);
      const secPart = Math.round(sec % 60);
      return `${min}:${secPart.toString().padStart(2, '0')}`;
    };

    const perMileToPerKm = (milePace: number) => milePace * 1.60934;

    setPaces({
      tPace: {
        perMile: formatPace(tPaceMileSec),
        perKm: formatPace(perMileToPerKm(tPaceMileSec)),
      },
      iPace: {
        perMile: formatPace(iPaceMileSec),
        perKm: formatPace(perMileToPerKm(iPaceMileSec)),
      },
    });
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Pace Calculator</h1>
        <p className="text-muted">Enter your 5K personal record to calculate your training paces.</p>
      </div>

      <div className="card-base p-8 mb-8">
        <div className="max-w-md">
          <label className="block text-sm font-medium text-[#e2e8f0] mb-2">5K Personal Record</label>
          <div className="flex space-x-4">
            <input
              type="text"
              placeholder="e.g., 20:00"
              value={fiveKTime}
              onChange={(e) => setFiveKTime(e.target.value)}
              className="flex-1"
            />
            <button
              onClick={calculatePaces}
              className="px-6 py-2 bg-[#3b82f6] text-white rounded-lg hover:bg-[#60a5fa] transition-colors"
            >
              Calculate
            </button>
          </div>
          <p className="text-sm text-[#718096] mt-2">Format: MM:SS (e.g., 20:00 for 20 minutes)</p>
        </div>
      </div>

      {paces && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="card-base p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-[rgba(59,130,246,0.1)] rounded-lg">
                <TrendingUp className="text-[#3b82f6]" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-semibold">T-Pace (Threshold)</h2>
                <p className="text-sm text-muted">Functional Threshold</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#0a0e14] rounded-lg p-4 text-center">
                <p className="text-sm text-muted mb-1">Per Mile</p>
                <p className="text-2xl font-bold text-[#3b82f6]">{paces.tPace.perMile}</p>
              </div>
              <div className="bg-[#0a0e14] rounded-lg p-4 text-center">
                <p className="text-sm text-muted mb-1">Per Km</p>
                <p className="text-2xl font-bold text-[#3b82f6]">{paces.tPace.perKm}</p>
              </div>
            </div>
          </div>

          <div className="card-base p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-[rgba(139,92,246,0.1)] rounded-lg">
                <TrendingUp className="text-[#8b5cf6]" size={24} />
              </div>
              <div>
                <h2 className="text-xl font-semibold">I-Pace (Interval)</h2>
                <p className="text-sm text-muted">VO2max Training</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-[#0a0e14] rounded-lg p-4 text-center">
                <p className="text-sm text-muted mb-1">Per Mile</p>
                <p className="text-2xl font-bold text-[#8b5cf6]">{paces.iPace.perMile}</p>
              </div>
              <div className="bg-[#0a0e14] rounded-lg p-4 text-center">
                <p className="text-sm text-muted mb-1">Per Km</p>
                <p className="text-2xl font-bold text-[#8b5cf6]">{paces.iPace.perKm}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-[rgba(59,130,246,0.1)] border border-[rgba(59,130,246,0.2)] rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Info className="text-[#3b82f6] mt-0.5" size={24} />
          <div>
            <h3 className="font-semibold mb-2">Important Training Note</h3>
            <p className="text-muted">
              Train at the prescribed pace for optimal results. Pushing harder than prescribed pace will only increase recovery time without improving training effect. For most triathletes, T-pace is closer to their open half marathon pace, not full marathon pace.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaceCalculator;
